import { FC, useCallback, useEffect, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import styles from './AppStatusIncidents.module.scss';
import ImageViewerModal from './ImageViewerModal';

type ImageDropzoneProps = {
  existingUrl?: string | null;
  id: string;
  label: string;
  onFileSelect: (file: File | null) => void;
  value?: File | null;
};

const ImageDropzone: FC<ImageDropzoneProps> = ({ existingUrl, id, label, onFileSelect, value }) => {
  const { t } = useTranslation();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!value) {
      setSelectedImageUrl(null);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(value);
    setSelectedImageUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [value]);

  const onDropAccepted = useCallback(
    (files: File[]) => {
      onFileSelect(files[0] ?? null);
      setRejectionMessage('');
    },
    [onFileSelect],
  );

  const onDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      if (rejections.length) setRejectionMessage(t('appStatus.form.invalidImage'));
    },
    [t],
  );

  const { getInputProps, getRootProps, isDragActive, open } = useDropzone({
    accept: { 'image/*': [] },
    maxFiles: 1,
    multiple: false,
    noClick: Boolean(value || existingUrl),
    onDropAccepted,
    onDropRejected,
  });

  const previewUrl = selectedImageUrl ?? existingUrl ?? null;

  return (
    <div className={styles['image-upload-field']}>
      <span className={styles['image-upload-label']}>{label}</span>
      <div
        {...getRootProps()}
        id={id}
        className={`${styles.dropzone} ${isDragActive ? styles['dropzone-active'] : ''}`}
      >
        <input {...getInputProps()} />
        {previewUrl ? (
          <div className={styles['dropzone-preview']}>
            <img src={previewUrl} alt={label} />
            <div className={styles['dropzone-file-info']}>
              <strong>{value?.name ?? t('appStatus.form.currentImage')}</strong>
              <span className={styles['dropzone-file-status']}>
                {value ? t('appStatus.form.selectedImage') : t('appStatus.form.savedImage')}
              </span>
            </div>
            <div className={styles['dropzone-actions']}>
              <button
                type="button"
                className={styles['dropzone-action-button']}
                onClick={(event) => {
                  event.stopPropagation();
                  setIsPreviewOpen(true);
                }}
                aria-label={t('appStatus.form.previewImage')}
                title={t('appStatus.form.previewImage')}
              >
                <i className="pi pi-eye" aria-hidden="true" />
              </button>
              <button
                type="button"
                className={styles['dropzone-action-button']}
                onClick={(event) => {
                  event.stopPropagation();
                  open();
                }}
                aria-label={t('appStatus.form.replaceImage')}
                title={t('appStatus.form.replaceImage')}
              >
                <i className="pi pi-upload" aria-hidden="true" />
              </button>
              {value && (
                <button
                  type="button"
                  className={styles['dropzone-action-button']}
                  onClick={(event) => {
                    event.stopPropagation();
                    onFileSelect(null);
                  }}
                  aria-label={t('appStatus.form.removeSelectedImage')}
                  title={t('appStatus.form.removeSelectedImage')}
                >
                  <i className="pi pi-times" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className={styles['dropzone-empty']}>
            <span className={styles['dropzone-upload-icon']}>
              <i className="pi pi-cloud-upload" aria-hidden="true" />
            </span>
            <strong>
              {isDragActive ? t('appStatus.form.dropImageHere') : t('appStatus.form.dragImage')}
            </strong>
            <span>{t('appStatus.form.selectImage')}</span>
          </div>
        )}
      </div>
      {rejectionMessage && <small>{rejectionMessage}</small>}
      {previewUrl && (
        <ImageViewerModal
          alt={label}
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          url={previewUrl}
        />
      )}
    </div>
  );
};

export default ImageDropzone;
